import React, {useState, useCallback, useMemo, useEffect} from "react";
import {inject, observer} from "mobx-react";
import {useNavigate, useLocation} from "react-router-dom";
//local
import {ReactNumberInput} from "components/ReactNumberInput";
import {Language} from "components/Language";
import {Button} from "components/buttons/Button";
import {InputsCode} from "components/InputsCode";
import {MAIN_PAGE, ONBOARDING} from "constants/routes.const";
import {useTranslation} from "react-i18next";
import {IconBankIdLogo, IconNotificationCircle} from "components/icons";
import Logo from "components/Header/components/Logo";
import {InfoStrip} from "components/InfoStrip";
import {CustomTooltip} from "components/CustomTooltip";
import {color} from "constants/color.consts";
import {showError, showWarning} from "helpers/notifications.helper";
import {getSessionStorage, removeSessionStorage} from "helpers/storage";
import {SESSION_ENDED, LOGIN_LINK_FAILED} from "constants/auth.const";
import {VideoInstruction} from "components/VideoInstruction";
import {IconMicrosoft} from "components/icons/IconMicrosoft";
import {Preloader} from "components/Preloader";
import {Info} from "components/Info";

const MAIN_PAGE_QUERY_PARAMS = ["language", "reason", "error_description", "entraToken"];

const MainPage = inject("store")(
  observer(({store: {auth, publicStore}}) => {
    const {commonSettings} = publicStore;
    const [isValidPhone, setIsValidPhone] = useState(false);
    const [phone, setPhone] = useState("");
    const [loginStep, setLoginStep] = useState(1);
    const [code, setCode] = useState(["", "", "", ""]);
    const [loginLinkSending, setLoginLinkSending] = useState(false);
    const [loginLinkSent, setLoginLinkSent] = useState(false);
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [entraDisabled, setEntraDisabled] = useState(false);
    const [isLoadingEntra, setIsLoadingEntra] = useState(false);
    const [entraErrorText, setEntraErrorText] = useState("");

    const clearEntraErrorQueryParams = useCallback(() => {
      const params = new URLSearchParams(location.search);
      const hasReason = params.has("reason");
      const hasErrorDescription = params.has("error_description");
      if (!hasReason && !hasErrorDescription) return;

      params.delete("reason");
      params.delete("error_description");
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
          hash: location.hash,
        },
        {replace: true}
      );
      setEntraErrorText("");
    }, [location.pathname, location.search, location.hash, navigate]);

    const submitNumber = useCallback(() => {
      clearEntraErrorQueryParams();
      setEntraDisabled(true);
      phone.length &&
        isValidPhone &&
        auth
          .sendNumber(phone)
          .then((data) => {
            if (data?.isEntraUser) return;
            const isUser = data?.isUser;
            !isUser && auth.setPhoneNumber(phone);
            isUser ? setLoginStep(2) : navigate(ONBOARDING);
            setEntraDisabled(false);
          })
          .catch((e) => {
            setPhone("");
            const is401Error = e?.response?.status === 401;
            !is401Error &&
              showError(e?.response?.data?.error || e?.message || "Unknown error");
            setEntraDisabled(false);
            setIsLoadingEntra(false);
          });
    }, [auth, phone, navigate, isValidPhone, clearEntraErrorQueryParams]);

    const isValidCode = useMemo(() => code.every(Boolean), [code]);

    const formError = (e) =>
      e?.response?.status === 400 &&
      e?.response?.data?.error.includes("code provided does not match")
        ? showError(t("LOGIN_SMS_CODE_ERROR"))
        : e?.response?.data?.error
        ? showError(e?.response?.data?.error)
        : showError("Unknown error");

    const submitCode = (e) => {
      e.preventDefault();
      const codeStr = code.join("");
      auth
        .checkCode(phone, codeStr, auth.request_id)
        .then((data) => data && data.token && navigate(MAIN_PAGE))
        .catch((e) => {
          formError(e);
          setCode(["", "", "", ""]);
        });
    };

    // "Didn't get an SMS?": a one-time login link to the verified email of the account
    const sendLoginLink = () => {
      setLoginLinkSending(true);
      auth
        .sendLoginLink(phone)
        .then(() => setLoginLinkSent(true))
        .catch(() => null)
        .finally(() => setLoginLinkSending(false));
    };

    // fallback texts until the translations are added
    const tWithFallback = (key, en, sv) =>
      t(key, {defaultValue: i18n.language === "en" ? en : sv});

    const onEntraLogin = () => {
      if (entraDisabled || isLoadingEntra) return;
      clearEntraErrorQueryParams();
      setEntraDisabled(true);
      setIsLoadingEntra(true);

      const restBase = process.env.REACT_APP_API_URL;
      const rootBase = restBase.replace(/\/v2\/?$/, "");

      const target = `${rootBase.replace(/\/$/, "")}/auth/entra/login`;

      if (typeof window !== "undefined") {
        window.location.href = target;
      } else {
        setEntraDisabled(false);
        setIsLoadingEntra(false);
      }
    };

    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const reason = params.get("reason");
      if (!reason) {
        setEntraErrorText("");
        return;
      }
      const errorDescription = params.get("error_description");
      setEntraErrorText(errorDescription || t(reason, {defaultValue: reason}));
    }, [location.search, t]);

    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const unknownKeys = [...params.keys()].filter(
        (key) => !MAIN_PAGE_QUERY_PARAMS.includes(key)
      );
      if (!unknownKeys.length) return;

      unknownKeys.forEach((key) => params.delete(key));
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
          hash: location.hash,
        },
        {replace: true}
      );
    }, [location.pathname, location.search, location.hash, navigate]);

    // set by the 401 handler when the session ended
    useEffect(() => {
      if (!getSessionStorage(SESSION_ENDED)) return;
      removeSessionStorage(SESSION_ENDED);
      showWarning(
        t("SESSION_ENDED_LOG_IN_AGAIN", {
          defaultValue:
            i18n.language === "en"
              ? "You have been logged out. Please log in again."
              : "Du har loggats ut. Logga in igen.",
        }),
        10
      );
    }, [t, i18n.language]);

    // set by the login link page when the link didn't work
    useEffect(() => {
      if (!getSessionStorage(LOGIN_LINK_FAILED)) return;
      removeSessionStorage(LOGIN_LINK_FAILED);
      showWarning(
        t("LOGIN_LINK_INVALID", {
          defaultValue:
            i18n.language === "en"
              ? "The login link is invalid, already used or has expired. Ask support for a new link or log in with your mobile number."
              : "Inloggningslänken är ogiltig, redan använd eller har gått ut. Be supporten om en ny länk eller logga in med ditt mobilnummer.",
        }),
        10
      );
    }, [t, i18n.language]);

    useEffect(() => {
      if (location.pathname === ONBOARDING && loginStep === 1)
        navigate(MAIN_PAGE, {replace: true});
    }, [location, loginStep, navigate]);

    return (
      <div className="main-page__wrapper">
        {commonSettings?.infoStrip?.active && (
          <InfoStrip color={color.green} text={commonSettings?.infoStrip?.text} />
        )}
        <div
          className="main-page__content"
          style={{marginTop: commonSettings?.infoStrip?.active ? "6vh" : "none"}}
        >
          {loginStep === 1 && (
            <React.Fragment>
              <div className="top">
                <div className="logo-language">
                  <Logo />
                  <Language position="bottomRight" />
                </div>
                <p>{t("LOGIN_PAGE_DESCRIPTION")}</p>
                <div className="main-page__content__form">
                  <ReactNumberInput
                    autoFocus
                    phone={phone}
                    setPhone={setPhone}
                    onEnterKeyPress={submitNumber}
                    setIsValid={setIsValidPhone}
                  />
                </div>
                <p className="sms-extra-info mt-2">
                  {t("USE_A_NUMBER_THAT_CAN_RECIEVE_SMS")}
                </p>
                <Button
                  onClick={submitNumber}
                  name={t("LOGIN_SEND_MESSAGE")}
                  disabled={
                    !phone.length || !isValidPhone || isLoadingEntra || entraDisabled
                  }
                />
                <CustomTooltip text={t("LOGIN_PAGE_ENTRA_LOGIN_TOOLTIP")}>
                  <span>
                    {/* tooltip does not fire without this extra span b/c Ant button disabled eats mouse events */}
                    <Button
                      type="button"
                      addClass="main-page__entra-button"
                      iconRight={<IconNotificationCircle />}
                      small
                      disabled={entraDisabled}
                      loading={isLoadingEntra}
                      icon={isLoadingEntra ? <Preloader /> : <IconMicrosoft />}
                      name={t("LOGIN_PAGE_ENTRA_LOGIN")}
                      onClick={() => onEntraLogin()}
                    />
                  </span>
                </CustomTooltip>
                <Info
                  text={
                    <>
                      <a
                        href={t("LOGIN_PAGE_SUPPORT_LINK_URL")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("LOGIN_PAGE_ENTRA_LOGIN_CONTACT_US")}
                      </a>{" "}
                      {t("LOGIN_PAGE_ENTRA_LOGIN_EXTRA_INFO")}
                    </>
                  }
                  addClass="mla mra"
                />
                {!!entraErrorText && (
                  <div
                    className="main-page__entra-error"
                    style={{color: "#d32f2f", marginTop: 8, fontSize: "0.9rem"}}
                  >
                    {entraErrorText}
                  </div>
                )}
              </div>

              <div className="bottom">
                <h4 className="mb-4">{t("LOGIN_PAGE_VIDEO_DESCRIPTION")}</h4>
                <VideoInstruction
                  url="https://player.vimeo.com/video/657058643"
                  size={{width: "640", height: "360"}}
                  allowFullScreen
                  addClass="mb-8"
                />
                <div className="instructions">
                  <div className="column">
                    <h4>{t("LOGIN_PAGE_INFO_COL1_HEADER")}</h4>
                    <ol>
                      <li>{t("LOGIN_PAGE_INFO_COL1_ITEM_1")}</li>
                      <li>{t("LOGIN_PAGE_INFO_COL1_ITEM_2")}</li>
                      <li>
                        {t("LOGIN_PAGE_INFO_COL1_ITEM_3")} <IconBankIdLogo />
                      </li>

                      <li>{t("LOGIN_PAGE_INFO_COL1_ITEM_4")}</li>
                    </ol>
                  </div>
                  <div className="column">
                    <h4>{t("LOGIN_PAGE_INFO_COL2_HEADER")}</h4>
                    <ol>
                      <li>{t("LOGIN_PAGE_INFO_COL2_ITEM_1")}</li>
                      <li>{t("LOGIN_PAGE_INFO_COL2_ITEM_2")}</li>
                    </ol>
                  </div>
                  <div className="column">
                    <h4>{t("LOGIN_PAGE_INFO_COL3_HEADER")}</h4>
                    <p>{t("LOGIN_PAGE_INFO_COL3_ITEM_1")}</p>
                    <p>{t("LOGIN_PAGE_INFO_COL3_ITEM_2")}</p>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
          {loginStep === 2 && (
            <React.Fragment>
              <form onSubmit={submitCode} className="main-page__content__auth-code">
                <h2>{t("AUTH_CODE_MODAL_TITLE")}</h2>
                <p>{t("AUTH_CODE_MODAL_TEXT")}</p>
                <InputsCode code={code} setCode={setCode} />
                <Button name={t("SIGN_IN")} disabled={!isValidCode} type="submit" />
                {/* "Didn't get an SMS?": the link emails a one-time login link */}
                {loginLinkSent ? (
                  <p className="main-page__no-sms mt-8 fs-12">
                    {tWithFallback(
                      "LOGIN_NO_SMS_LINK_SENT",
                      "If your account has a verified email address, we have sent a login link to it. The link is valid for 10 minutes.",
                      "Om ditt konto har en verifierad e-postadress har vi skickat en inloggningslänk dit. Länken gäller i 10 minuter."
                    )}
                  </p>
                ) : (
                  <p className="main-page__no-sms mt-8 fs-12">
                    {tWithFallback(
                      "AUTH_CODE_EMAIL_FALLBACK_TEXT_1",
                      "Didn't get an SMS?",
                      "Fick du inget SMS?"
                    )}{" "}
                    <button
                      type="button"
                      className="blue"
                      onClick={sendLoginLink}
                      disabled={loginLinkSending}
                    >
                      {tWithFallback(
                        "AUTH_CODE_EMAIL_FALLBACK_LINK",
                        "Click here",
                        "Klicka här"
                      )}
                    </button>{" "}
                    {tWithFallback(
                      "AUTH_CODE_EMAIL_FALLBACK_TEXT_3",
                      "to get a login link to your email.",
                      "för att få en inloggningslänk till din e-post."
                    )}
                  </p>
                )}
              </form>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  })
);

export default MainPage;
